// src/contexts/ChatContext.js

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "../hooks/useAuth";
import { useCollection } from "../hooks/useCollection";
import { useFirestore } from "../hooks/useFirestore";

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error("useChat debe usarse dentro de ChatProvider");
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const { data: users } = useCollection("users");
  const { data: chatRooms } = useCollection("chatRooms");
  const { data: messages } = useCollection("chatMessages");
  const { add: addRoom, update: updateRoom } = useFirestore("chatRooms");
  const { add: addMessage, update: updateMessage } = useFirestore("chatMessages");
  const { data: readReceipts } = useCollection("chatReadReceipts");
  const { add: addReadReceipt, update: updateReadReceipt } = useFirestore("chatReadReceipts");

  const [isOpen, setIsOpen] = useState(false);
  const [activeRoom, setActiveRoom] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  
  // Ref para guardar el último timestamp de lectura por sala
  const lastReadTimestamp = useRef({});

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
    setShowNewChat(false);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setActiveRoom(null);
    setShowNewChat(false);
  }, []);

  // Marcar mensajes como leídos al entrar en una sala
  useEffect(() => {
    if (!activeRoom?.id || !user?.uid || !messages) return;

    const markMessagesAsRead = async () => {
      const unreadMessages = messages.filter(
        (m) =>
          m.roomId === activeRoom.id &&
          m.senderId !== user.uid &&
          !m.readBy?.includes(user.uid)
      );

      if (unreadMessages.length === 0) return;

      // Actualizar cada mensaje no leído
      const updatePromises = unreadMessages.map((msg) => {
        const updatedReadBy = [...(msg.readBy || []), user.uid];
        return updateMessage(msg.id, {
          readBy: updatedReadBy,
        });
      });

      await Promise.all(updatePromises);
      
      // Actualizar el timestamp de última lectura
      lastReadTimestamp.current[activeRoom.id] = new Date().toISOString();
    };

    markMessagesAsRead();
  }, [activeRoom?.id, user?.uid, messages]);

  // Salas donde el usuario es miembro
  const myRooms =
    chatRooms?.filter(
      (room) => room.members?.includes(user?.uid) && room.isActive !== false,
    ) || [];

  // Mensajes de la sala activa
  const roomMessages =
    messages
      ?.filter((m) => m.roomId === activeRoom?.id)
      ?.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)) || [];

  // Usuarios disponibles para chatear (excluyéndose a uno mismo)
  const availableUsers =
    users?.filter((u) => u.id !== user?.uid && u.isActive !== false) || [];

  const startDirectChat = async (otherUserId) => {
    // Buscar si ya existe una sala directa entre estos dos usuarios
    const existingRoom = chatRooms?.find(
      (room) =>
        room.type === "direct" &&
        room.members?.includes(user?.uid) &&
        room.members?.includes(otherUserId) &&
        room.isActive !== false,
    );

    if (existingRoom) {
      setActiveRoom(existingRoom);
      setShowNewChat(false);
      return;
    }

    const otherUser = users?.find((u) => u.id === otherUserId);
    const newRoom = {
      name: null,
      type: "direct",
      members: [user?.uid, otherUserId],
      memberNames: [
        userProfile?.displayName || user.email,
        otherUser?.displayName || otherUser?.email,
      ],
      createdBy: user?.uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      lastMessage: null,
      lastMessageTime: null,
    };

    const roomRef = await addRoom(newRoom);
    setActiveRoom({ id: roomRef.id, ...newRoom });
    setShowNewChat(false);
  };

  const createGroup = async (name, memberIds) => {
    const memberNames = memberIds.map((id) => {
      const u = users?.find((u) => u.id === id);
      return u?.displayName || u?.email || "Usuario";
    });

    const newRoom = {
      name,
      type: "group",
      members: [user.uid, ...memberIds],
      memberNames: [userProfile?.displayName || user.email, ...memberNames],
      createdBy: user.uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      lastMessage: null,
      lastMessageTime: null,
    };

    const roomRef = await addRoom(newRoom);
    setActiveRoom({ id: roomRef.id, ...newRoom });
    setShowNewChat(false);
  };

  const sendMessage = async (text) => {
    if (!text.trim() || !activeRoom) return;

    const messageData = {
      text,
      senderId: user?.uid,
      senderName: userProfile?.displayName || user.email,
      senderAvatar: userProfile?.avatar || "",
      roomId: activeRoom.id,
      timestamp: new Date().toISOString(),
      readBy: [user?.uid], // El remitente ya lo ha leído
    };

    await addMessage(messageData);

    // Actualizar último mensaje de la sala
    await updateRoom(activeRoom.id, {
      lastMessage: text.substring(0, 100),
      lastMessageTime: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  const getRoomName = (room) => {
    if (room.name) return room.name;

    if (room.type === "direct") {
      const otherMemberName = room.memberNames?.find(
        (_, i) => room.members?.[i] !== user?.uid,
      );

      return otherMemberName || "Chat";
    }

    return "Chat";
  };

  const getUnreadCount = (roomId) => {
    if (!messages || !user?.uid) return 0;
    
    return messages.filter(
      (m) =>
        m.roomId === roomId &&
        m.senderId !== user.uid &&
        !m.readBy?.includes(user.uid),
    ).length;
  };

  // Calcular totalUnread en tiempo real
  const totalUnread = myRooms.reduce((sum, room) => {
    return sum + getUnreadCount(room.id);
  }, 0);

  const value = {
    isOpen,
    activeRoom,
    showNewChat,
    myRooms,
    roomMessages,
    availableUsers,
    totalUnread,
    toggleChat,
    openChat,
    closeChat,
    setActiveRoom,
    setShowNewChat,
    startDirectChat,
    createGroup,
    sendMessage,
    getRoomName,
    getUnreadCount,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};